import { useCallback, useEffect, useState } from 'react';
import useShare from './useShare';

export interface ReengagementPreview {
    total: number;
    textCount: number;
    emailCount: number;
    optedOutCount: number;
}

export interface ReengagementSendObject {
    id: number;
    activationId: number;
    channels: 'text' | 'email' | 'both';
    textBody: string | null;
    emailSubject: string | null;
    emailBody: string | null;
    status: 'pending' | 'running' | 'complete' | 'failed';
    totalCount: number;
    sentCount: number;
    failedCount: number;
    skippedCount: number;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
}

export interface StartReengagementPayload {
    channels: 'text' | 'email' | 'both';
    textBody?: string;
    emailSubject?: string;
    emailBody?: string;
}

const POLL_INTERVAL_MS = 2000;

/**
 * Shared state machine for the reengagement (reshare blast) tool: recipient
 * preview, kicking off a send, and polling the running send for progress.
 * Both the classic and v2 UIs render on top of this.
 */
export default function useReengagement(activationId: number) {
    const {
        getReengagementPreview,
        startReengagementSend,
        getReengagementSend,
        listReengagementSends,
    } = useShare();

    const [preview, setPreview] = useState<ReengagementPreview | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [sends, setSends] = useState<ReengagementSendObject[]>([]);
    const [activeSend, setActiveSend] = useState<ReengagementSendObject | null>(
        null,
    );
    const [isStarting, setIsStarting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPreview = useCallback(async () => {
        setIsLoadingPreview(true);
        try {
            setPreview(await getReengagementPreview(activationId));
        } catch {
            setError('Could not load recipients');
        } finally {
            setIsLoadingPreview(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const loadSends = useCallback(async () => {
        try {
            setSends(await listReengagementSends(activationId));
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const start = useCallback(
        async (payload: StartReengagementPayload) => {
            if (isStarting) return null;
            setIsStarting(true);
            setError(null);
            try {
                const send = await startReengagementSend(activationId, payload);
                setActiveSend(send);
                return send;
            } catch (e: any) {
                setError(
                    e?.response?.data || 'Could not start the reengagement send',
                );
                return null;
            } finally {
                setIsStarting(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activationId, isStarting],
    );

    /* Poll the active send until it finishes. */
    const activeStatus = activeSend?.status;
    const activeId = activeSend?.id;
    useEffect(() => {
        if (
            !activeId ||
            (activeStatus !== 'pending' && activeStatus !== 'running')
        ) {
            return;
        }

        const interval = setInterval(async () => {
            try {
                const send = await getReengagementSend(activeId);
                setActiveSend(send);
                if (send.status === 'complete' || send.status === 'failed') {
                    loadSends();
                }
            } catch {}
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId, activeStatus]);

    return {
        preview,
        isLoadingPreview,
        loadPreview,
        sends,
        loadSends,
        activeSend,
        setActiveSend,
        isStarting,
        start,
        error,
    };
}
